// src/llmService.js
/**
 * Client-side LLM calling module.
 * Builds prompts, calls provider APIs directly from the browser,
 * parses responses, and returns structured move data.
 * API keys never leave the browser.
 */
import { TILE_TYPE, renderLabeledBoardLines } from './utils';

// ---------------------------------------------------------------------------
// Provider configurations
// ---------------------------------------------------------------------------
const PROVIDER_CONFIGS = {
  OpenAI:     { type: 'openai',    baseUrl: 'https://api.openai.com/v1' },
  XAI:        { type: 'openai',    baseUrl: 'https://api.x.ai/v1' },
  OpenRouter: { type: 'openai',    baseUrl: 'https://openrouter.ai/api/v1' },
  Anthropic:  { type: 'anthropic', baseUrl: 'https://api.anthropic.com' },
  Google:     { type: 'google',    baseUrl: 'https://generativelanguage.googleapis.com' },
};

// USD per million tokens. Rough estimates for cost tracking, update as prices shift.
// Matched by first prefix that startsWith — so list specific variants BEFORE
// their general family (e.g. claude-opus-4-7 before claude-opus-4).
const PRICE_TABLE = [
  // Anthropic (input, output per 1M tokens)
  // Fable is the $10/$50 tier; Opus 5 and 4.5-4.8 are $5/$25; Opus 4 and 4.1 are still $15/$75.
  { prefix: 'claude-fable-5',    input: 10,  output: 50 },
  { prefix: 'claude-opus-5',     input: 5,   output: 25 },
  { prefix: 'claude-sonnet-5',   input: 2,   output: 10 },
  { prefix: 'claude-opus-4-8',   input: 5,   output: 25 },
  { prefix: 'claude-opus-4-7',   input: 5,   output: 25 },
  { prefix: 'claude-opus-4-6',   input: 5,   output: 25 },
  { prefix: 'claude-opus-4-5',   input: 5,   output: 25 },
  { prefix: 'claude-opus-4-1',   input: 15,  output: 75 },
  { prefix: 'claude-opus-4',     input: 15,  output: 75 },
  { prefix: 'claude-sonnet-4',   input: 3,   output: 15 },
  { prefix: 'claude-haiku-4',    input: 1,   output: 5 },
  { prefix: 'claude-haiku-3-5',  input: 0.8, output: 4 },
  { prefix: 'claude-haiku-3',    input: 0.25, output: 1.25 },
  // OpenAI
  { prefix: 'gpt-5.5-pro',      input: 30,   output: 180 },
  { prefix: 'gpt-5.5',          input: 5,    output: 30 },
  { prefix: 'gpt-5.4-pro',      input: 30,   output: 180 },
  { prefix: 'gpt-5.4-mini',     input: 0.75, output: 4.5 },
  { prefix: 'gpt-5.4-nano',     input: 0.20, output: 1.25 },
  { prefix: 'gpt-5.4',          input: 2.5,  output: 15 },
  { prefix: 'gpt-5',            input: 2.5,  output: 10 },
  { prefix: 'gpt-4o-mini',      input: 0.15, output: 0.6 },
  { prefix: 'gpt-4o',           input: 2.5,  output: 10 },
  // Google. List specific variants BEFORE the gemini-3 family fallback.
  // Order within a family: newest first, then flash-lite, then base.
  { prefix: 'gemini-3.5-flash',         input: 1.5,  output: 9 },
  { prefix: 'gemini-3.1-pro',           input: 2,    output: 12 },
  { prefix: 'gemini-3.1-flash-lite',    input: 0.25, output: 1.5 },
  { prefix: 'gemini-3.1-flash',         input: 0.5,  output: 3 },
  { prefix: 'gemini-3-flash',           input: 0.5,  output: 3 },
  { prefix: 'gemini-3-pro',             input: 2,    output: 12 },
  { prefix: 'gemini-2.5-pro',           input: 1.25, output: 10 },
  { prefix: 'gemini-2.5-flash-lite',    input: 0.1,  output: 0.4 },
  { prefix: 'gemini-2.5-flash',         input: 0.3,  output: 2.5 },
  { prefix: 'gemini-2.5',               input: 0.3,  output: 2.5 },
  // xAI — list specific variants before the grok-4 family fallback.
  { prefix: 'grok-4.3',         input: 1.25, output: 2.5 },
  { prefix: 'grok-4.1-fast',    input: 0.2,  output: 0.5 },
  { prefix: 'grok-4-fast',      input: 0.2,  output: 0.5 },
  { prefix: 'grok-4',           input: 3,    output: 15 },
  { prefix: 'grok-code',        input: 0.2,  output: 1.5 },
  { prefix: 'grok-3-mini',      input: 0.3,  output: 0.5 },
  { prefix: 'grok-3',           input: 3,    output: 15 },
  // OpenRouter-routed models (per OpenRouter's live model registry, May 2026)
  { prefix: 'moonshotai/kimi-k2.6',         input: 0.75,  output: 3.5 },
  { prefix: 'xiaomi/mimo-v2-pro',           input: 1.0,   output: 3.0 },
  { prefix: 'deepseek/deepseek-v3.2',       input: 0.252, output: 0.378 },
  { prefix: 'qwen/qwen3.6-plus',            input: 0.325, output: 1.95 },
  { prefix: 'meta-llama/llama-4-maverick',  input: 0.15,  output: 0.6 },
  { prefix: 'meta-llama/llama-4-scout',     input: 0.08,  output: 0.3 },
  { prefix: 'mistralai/mistral-large-2512', input: 0.5,   output: 1.5 },
];

// Quick character-based token estimate. Anthropic and OpenAI average about
// 4 characters per token for English/ASCII prose. Gemini runs closer to 4.2.
// This is a rough estimate; use provider countTokens endpoints for precision.
export function estimateTokens(text) {
  if (!text) return 0;
  return Math.ceil(text.length / 4);
}

// Price lookup exposed so UIs can display per-turn/per-game cost estimates.
export function lookupPrice(model) {
  if (!model) return null;
  return PRICE_TABLE.find(p => model.startsWith(p.prefix)) || null;
}

function estimateCostUsd(model, usage) {
  if (!usage || !model) return 0;
  const entry = PRICE_TABLE.find(p => model.startsWith(p.prefix));
  if (!entry) return 0;
  // Cached tokens typically cost 10% of input on most providers; approximate.
  const billedInput = Math.max(0, (usage.input || 0) - (usage.cached || 0));
  const inputCost = (billedInput / 1_000_000) * entry.input;
  const cachedCost = ((usage.cached || 0) / 1_000_000) * entry.input * 0.1;
  // Reasoning tokens are billed at output rate on OpenAI/Gemini
  const outputCost = ((usage.output || 0) / 1_000_000) * entry.output;
  return +(inputCost + cachedCost + outputCost).toFixed(6);
}

// ---------------------------------------------------------------------------
// API call implementations
// ---------------------------------------------------------------------------

async function callOpenAI(baseUrl, apiKey, model, systemPrompt, userPrompt, provider) {
  const headers = {
    'Authorization': `Bearer ${apiKey}`,
    'Content-Type': 'application/json',
  };
  if (provider === 'OpenRouter') {
    headers['HTTP-Referer'] = window.location.origin;
  }
  // OpenAI auto-caches exact prefixes > 1024 tokens. Keeping the large
  // tutorial in a dedicated system message makes every subsequent turn
  // hit the cache (50% input discount for cached portion).
  const messages = [];
  if (systemPrompt) messages.push({ role: 'system', content: systemPrompt });
  messages.push({ role: 'user', content: userPrompt });

  // GPT-5 family and o-series are reasoning models: they reject
  // `max_tokens` (renamed to `max_completion_tokens`, which also covers
  // reasoning). OpenAI reserves the FULL max_completion_tokens against
  // your per-minute TPM budget at request time, so setting this too
  // high (e.g. 16384) starves the TPM quota and produces cascading 429s
  // on lower tiers. 8192 is plenty for a few-thousand-token reasoning
  // trace plus the short REASONING/MOVE response block.
  //
  // reasoning_effort: "low" keeps the trace short. The puzzle/play task
  // is structurally simple (find a legal move toward an endzone) and
  // doesn't benefit from "medium" or "high" effort, which inflate the
  // trace linearly with the board's piece count and make later turns
  // visibly slower than the opening on the same game.
  const isReasoningModel = /^gpt-5/i.test(model) || /^o[0-9]/i.test(model);
  const body = {
    model,
    messages,
    ...(isReasoningModel
        ? { max_completion_tokens: 8192, reasoning_effort: 'low' }
        : { max_tokens: 2048 }),
  };

  const res = await fetch(`${baseUrl}/chat/completions`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${provider} API error ${res.status}: ${text}`);
  }
  const data = await res.json();
  const u = data.usage;
  const usage = u ? {
    input: u.prompt_tokens ?? 0,
    output: u.completion_tokens ?? 0,
    reasoning: u.completion_tokens_details?.reasoning_tokens ?? 0,
    cached: u.prompt_tokens_details?.cached_tokens ?? 0,
  } : null;
  return { text: data.choices[0].message.content, usage };
}

// Thinking config by model family. 4.5-era models (haiku-4-5, sonnet-4-5,
// opus-4-5) take a fixed budget; 4.6 and later take adaptive thinking and
// reject budget_tokens. Sonnet 5, Opus 5 and Fable think by default, so
// this also keeps their answer from being starved by a small max_tokens.
const ANTHROPIC_MAX_TOKENS = 16000;
function anthropicThinking(model) {
  if (/-4-5\b/.test(model)) {
    return { type: 'enabled', budget_tokens: 8000 };
  }
  return { type: 'adaptive' };
}

async function callAnthropic(baseUrl, apiKey, model, systemPrompt, userPrompt) {
  // Explicit prompt caching: mark the static rules/tutorial block so
  // subsequent turns within 5 minutes bill the cached portion at 10%
  // of the normal input rate.
  const body = {
    model,
    messages: [{ role: 'user', content: userPrompt }],
    max_tokens: ANTHROPIC_MAX_TOKENS,
    thinking: anthropicThinking(model),
  };
  if (systemPrompt) {
    body.system = [{
      type: 'text',
      text: systemPrompt,
      cache_control: { type: 'ephemeral' },
    }];
  }
  const res = await fetch(`${baseUrl}/v1/messages`, {
    method: 'POST',
    headers: {
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Anthropic API error ${res.status}: ${text}`);
  }
  const data = await res.json();
  const u = data.usage;
  const usage = u ? {
    input: u.input_tokens ?? 0,
    output: u.output_tokens ?? 0,
    reasoning: 0,
    cached: (u.cache_read_input_tokens ?? 0) + (u.cache_creation_input_tokens ?? 0),
  } : null;
  // With thinking on, content[0] is a thinking block; take the text blocks.
  const text = (data.content || [])
    .filter(b => b.type === 'text')
    .map(b => b.text)
    .join('');
  if (!text && data.stop_reason === 'max_tokens') {
    throw new Error(`Anthropic response truncated: ${u?.output_tokens ?? '?'} output tokens spent entirely on thinking`);
  }
  // Fable-class models can decline via a safety classifier: HTTP 200 with
  // stop_reason "refusal" and no move. Say so instead of "unparseable".
  if (data.stop_reason === 'refusal') {
    const why = data.stop_details?.category || 'unspecified';
    throw new Error(`Anthropic model declined the request (refusal, category: ${why})`);
  }
  return { text, usage };
}

async function callGoogle(baseUrl, apiKey, model, systemPrompt, userPrompt) {
  const url = `${baseUrl}/v1beta/models/${model}:generateContent?key=${apiKey}`;
  // Gemini separates system instructions from user content. Explicit
  // prefix caching requires the Files API (not wired here); at minimum
  // isolating the tutorial into systemInstruction lets Gemini's
  // implicit context-caching recognize the shared prefix.
  const body = {
    contents: [{ role: 'user', parts: [{ text: userPrompt }] }],
  };
  if (systemPrompt) {
    body.systemInstruction = { parts: [{ text: systemPrompt }] };
  }
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Google API error ${res.status}: ${text}`);
  }
  const data = await res.json();
  const meta = data.usageMetadata;
  const usage = meta ? {
    input: meta.promptTokenCount ?? 0,
    output: meta.candidatesTokenCount ?? 0,
    reasoning: meta.thoughtsTokenCount ?? 0,
    cached: meta.cachedContentTokenCount ?? 0,
  } : null;
  return { text: data.candidates[0].content.parts[0].text, usage };
}

async function callProvider(provider, model, apiKey, systemPrompt, userPrompt) {
  const cfg = PROVIDER_CONFIGS[provider];
  if (!cfg) throw new Error(`Unknown provider: ${provider}`);

  switch (cfg.type) {
    case 'openai':
      return callOpenAI(cfg.baseUrl, apiKey, model, systemPrompt, userPrompt, provider);
    case 'anthropic':
      return callAnthropic(cfg.baseUrl, apiKey, model, systemPrompt, userPrompt);
    case 'google':
      return callGoogle(cfg.baseUrl, apiKey, model, systemPrompt, userPrompt);
    default:
      throw new Error(`Unknown provider type: ${cfg.type}`);
  }
}

// ---------------------------------------------------------------------------
// Board text rendering (port of phutball_env.py text_render)
// ---------------------------------------------------------------------------

const TILE_CHARS = {
  [TILE_TYPE.EMPTY]:        '┼',
  [TILE_TYPE.PHUTBALL]:     'O',
  [TILE_TYPE.PHUTBALL_MAN]: 'x',
  [TILE_TYPE.END_HI]:       '+',
  [TILE_TYPE.END_LO]:       '-',
};

function renderBoardText(board, currentPlayer, rows, cols, turnNumber, jumpSequenceActive, isPuzzle = false) {
  // Always render in absolute matrix coordinates. Coordinate labels on
  // top/left make positions unambiguous so the LLM doesn't spend tokens
  // converting between player-relative and absolute frames.
  const grid = [];
  for (let r = 0; r < rows; r++) {
    let rowStr = '';
    for (let c = 0; c < cols; c++) {
      rowStr += TILE_CHARS[board[r][c]] ?? '?';
    }
    grid.push(rowStr);
  }

  const labeled = renderLabeledBoardLines(grid, cols);

  // For puzzle positions, omit the turn number; it primes the LLM into
  // "this is the opening, board must be empty, I should place" reasoning even
  // though the board clearly isn't empty. Keep PLAYER so it knows whose move.
  const header = isPuzzle
    ? `PLAYER: ${currentPlayer}`
    : `TURN: ${turnNumber + 1} PLAYER: ${currentPlayer}`;
  const jumpInfo = jumpSequenceActive ? 'JUMP SEQ ACTIVE' : '';

  const lines = ['<board_state>', header];
  if (jumpInfo) lines.push(jumpInfo);
  lines.push(...labeled);
  lines.push('</board_state>');

  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Full 37-step tutorial (from Tutorial.js), included in every prompt
// ---------------------------------------------------------------------------

const TUTORIAL_TEXT = `\
PHUTBALL TUTORIAL

## 1. Introduction

Philosopher's Football is a two-player zero-sum game with simple rules and complex tactics. Players take turns placing stones on the board and consuming them in jump sequences to drive the ball across their opponent's endzone. Player 1 scores by landing in +++ (top, rows 0-1); Player 2 scores by landing in --- (bottom, rows 19-20). The board is always shown in absolute matrix coordinates (row 0 at top, row 20 at bottom).

BOARD FORMAT: every board shown in this tutorial and every live board state uses the same labeled layout. Two header rows at the top carry the column index (tens digit, then ones digit). Two-character labels on the left carry the row index. Read any position by looking straight up for the column and straight left for the row.

Starting position, ball (O) at center, empty board:

+++++++++++++++
+++++++++++++++
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼O┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---------------
---------------

Rows 0-1 are +++ (P1 scoring endzone). Rows 19-20 are --- (P1 defensive endzone). The ball starts at row 10, col 7.

## 2. Placement

On your turn you may place a stone (x) on any empty cell. Stones are neutral, both players place and jump the same pieces. A stone you place might help your opponent.

OPENING: At game start, the board has no stones, so the first player must PLACE.

Move: PLACE (9,7)

After:

+++++++++++++++
+++++++++++++++
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼O┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---------------
---------------

## 3. Forbidden Placements

You cannot place stones on the ball's position or in the deep endzone rows (rows 0-1 and 19-20).

## 3b. Placement Is Not Defense, It Is Fuel

Stones are shared. Whoever jumps next uses them. Placing a stone near the ball does NOT block your opponent; it hands them a jump. There is no such thing as a "defensive wall" in Phutball. If the ball is in play and you place a stone toward your own endzone, on your opponent's next turn they may jump directly over that stone and land closer to their scoring zone, against you.

Examples of placements that are essentially gifts to the opponent:

- You are P2 (score at bottom, rows 19-20). Ball is at (10,7). You place a stone at (3,7), hoping to "block" P1's attack. That placement is seven rows above the ball, isolated, and does nothing defensively. On P1's turn, every existing stone near the ball is still available for them to jump over, you have accomplished nothing.
- You are P2. Ball is at (10,7). You place a stone at (9,7), the cell immediately north of the ball, thinking it will "block" P1. Instead, on P1's next turn they jump N over (9,7) and land at (8,7), two rows closer to their +++ endzone, for free.
- You are P1. Ball is at (10,7). You place at (11,7), thinking you are preparing your own future attack, but P2 on their next turn jumps S over (11,7) and lands at (12,7), two rows closer to --- for free.

Correct uses of placement always build off of what is already on the board:

1. **Extend your own line.** If there are already stones aligned in the direction you want to jump, add one more at the far end so that a future jump, when the ball is in position and it is your turn, clears a longer chain and advances further. A lone stone in empty territory cannot be extended quickly enough to matter.
2. **Disrupt a planned landing or pivot.** A jump cannot end on an occupied cell — placing a stone on the cell where the opponent planned to land *forces the ball to continue past it*, extending the line further in that direction. This is useful only when the continuation lands somewhere they cannot exploit:
   - The cell was a **multi-hop pivot** — the hop in a planned sequence where the ball lands and changes direction. Forcing the ball past it means they cannot pivot there, so the rest of the chain is wasted (see §13 for the canonical example).
   - The continuation lands in **undeveloped space**, off the line they had built, with no nearby stones to bridge the ball back to its intended path.

   If neither holds, you have just added fuel: the ball clears a longer chain and lands further along in the direction they wanted to go.

Default check before placing: ask "can my opponent jump over this stone on their next turn in a way that helps them score, and if so is that gain less than what I gain from this placement?" If the answer favors them, do not place it there. "Defensive placement near the ball" that is not tied to an existing line or pivot is almost always a mistake.

## 4. Eight Doors

Every position has eight doors: N, NE, E, SE, S, SW, W, NW. Each door can only be used once per jump sequence. Here the ball is surrounded by 8 stones, one in each direction:

+++++++++++++++
+++++++++++++++
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼xxx┼┼┼┼┼┼
┼┼┼┼┼┼xOx┼┼┼┼┼┼
┼┼┼┼┼┼xxx┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---------------
---------------

## 5. Jumping Rules

The ball only moves by jumping over stones. You cannot move the ball to an empty adjacent cell, it must leap over at least one stone. Jumped stones are consumed (removed from the board).

Before (one stone north of ball):

+++++++++++++++
+++++++++++++++
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼O┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---------------
---------------

Move: JUMP (8,7) -> HALT

After (stone consumed, ball lands at row 8):

+++++++++++++++
+++++++++++++++
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼O┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---------------
---------------

## 6. Clearing Lines

A single jump clears every stone along a contiguous line. The ball leaps over the entire line and lands on the first empty position beyond.

Before (4 stones in a vertical line north of ball):

+++++++++++++++
+++++++++++++++
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼O┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---------------
---------------

Move: JUMP (5,7) -> HALT

After (all 4 stones consumed, ball lands at row 5):

+++++++++++++++
+++++++++++++++
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼O┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---------------
---------------

## 6b. Scoring

A turn that ends with the ball in the opponent's endzone wins the game (rows 0-1 for P1, rows 19-20 for P2). A turn that ends with the ball in your own endzone loses the game. As §12 shows, you can pass through any endzone mid-sequence. Only the final landing position determines win/loss.

## 7. The Long Kick

Multiple jumps can be chained in different directions. Each jump consumes the stones it crosses. A path can zigzag up the field to score.

Before (7 stones in a zigzag path, ball at row 10 col 7):

+++++++++++++++
++++++x++++++++
┼┼┼┼┼x┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼x┼┼┼┼┼┼┼┼┼┼
┼┼┼┼x┼┼┼┼┼┼┼┼┼┼
┼┼┼┼x┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼x┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼x┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼O┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---------------
---------------

Move: JUMP (7,4) -> (3,4) -> (0,7) -> HALT

Jump 1: NW from (10,7), over stones at (9,6) and (8,5), land at (7,4).
Jump 2: N from (7,4), over stones at (6,4), (5,4), (4,4), land at (3,4).
Jump 3: NE from (3,4), over stones at (2,5) and (1,6), land at (0,7), in the +++ endzone!

After (all 7 stones consumed, ball scores at row 0):

+++++++O+++++++
+++++++++++++++
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---------------
---------------

## 8. Invalid Jumps

A jump is invalid if there is no stone to jump over in the chosen direction. The ball cannot move to an empty adjacent cell.

## 9. Halting Early

You do not have to complete every possible jump. Sometimes stopping early is strategically correct. There is no "pass", you must place a stone or make at least one jump.

Before (3 stones north of ball at rows 5, 7, 9):

+++++++++++++++
+++++++++++++++
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼O┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---------------
---------------

Move: JUMP (8,7) -> HALT

After (only the first stone consumed, ball stops at row 8, two stones remain):

+++++++++++++++
+++++++++++++++
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼O┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---------------
---------------

## 10. The Futile Loop

A jump sequence can consume pieces and return the ball to its starting position. This clears stones but gains no ground.

Before (3 stones in a triangle, at (9,7), (8,8), and (9,8)):

+++++++++++++++
+++++++++++++++
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼x┼┼┼┼┼┼
┼┼┼┼┼┼┼xx┼┼┼┼┼┼
┼┼┼┼┼┼┼O┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---------------
---------------

Move: JUMP (8,7) -> (8,9) -> (10,7) -> HALT

Jump 1: N over stone at (9,7), land at (8,7).
Jump 2: E over stone at (8,8), land at (8,9).
Jump 3: SW over stone at (9,8), land at (10,7), back where we started!

After (all 3 stones consumed, ball returns to start):

+++++++++++++++
+++++++++++++++
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼O┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---------------
---------------

## 11. The Visit Limit

The ball can visit the same position up to five times in one sequence (once at the start, plus four returns). Each return uses a different pair of doors.

Before (12 stones in a star pattern around the ball):

+++++++++++++++
+++++++++++++++
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼x┼┼┼┼┼┼
┼┼┼┼┼xxxx┼┼┼┼┼┼
┼┼┼┼┼┼xOx┼┼┼┼┼┼
┼┼┼┼┼┼xxxx┼┼┼┼┼
┼┼┼┼┼┼x┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---------------
---------------

Move: JUMP (8,7) -> (8,9) -> (10,7) -> (12,7) -> (12,5) -> (10,7) -> (12,9) -> (10,9) -> (10,7) -> (8,5) -> (10,5) -> (10,7) -> HALT

This 12-jump sequence returns to center on jumps 3, 6, 9, and 12, using all eight doors.

After (all 12 stones consumed, ball ends where it began):

+++++++++++++++
+++++++++++++++
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼O┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---------------
---------------

## 12. Own Goals and Escape

If your jump ends in your own endzone (--- for P1), you lose. But you can pass through your own endzone mid-sequence and escape.

Before (10 stones forming a V, SW down to endzone, then NE back up):

+++++++++++++++
+++++++++++++++
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼O┼┼┼┼┼┼┼
┼┼┼┼┼┼x┼┼┼┼┼x┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼x┼┼┼┼┼x┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼x┼┼┼┼x┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼x┼┼x┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---xx----------
---------------

Move: JUMP (12,5) -> (14,3) -> (16,3) -> (18,3) -> (20,3) -> (18,5) -> (16,7) -> (14,9) -> (12,11) -> (10,13) -> HALT

The ball dives SW into the --- endzone at (20,3), then escapes NE back to (10,13). The endzone is only deadly if you END there.

After (all 10 stones consumed, ball safe at row 10 col 13):

+++++++++++++++
+++++++++++++++
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼O┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---------------
---------------

## 13. Tackling

Your opponent has built an attack toward your endzone. You can disrupt their chain by placing a stone at the pivot — the cell where they planned to land and change direction — extending their jump so it overshoots.

Before (ball at row 8 col 9, stones forming a SW-then-S path):

+++++++++++++++
+++++++++++++++
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼O┼┼┼┼┼
┼┼┼┼┼┼┼┼x┼┼┼┼┼┼
┼┼┼┼┼┼┼┼x┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---------------
---------------

The opponent wants: JUMP SW to (11,7), then S through rows 13, 15, 17 toward ---. You place a stone at (10,7) to extend the SW jump past the pivot.

Move: PLACE (10,7)

After:

+++++++++++++++
+++++++++++++++
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼O┼┼┼┼┼
┼┼┼┼┼┼┼┼x┼┼┼┼┼┼
┼┼┼┼┼┼┼xx┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
┼┼┼┼┼┼┼x┼┼┼┼┼┼┼
┼┼┼┼┼┼┼┼┼┼┼┼┼┼┼
---------------
---------------

Now their SW jump from (8,9) crosses stones at (9,8), (10,7), overshooting to (11,6) instead of stopping at the pivot. The S chain below is broken.

One last bit of wisdom from Conway: try not to jump until you really have to, and then only as far as you really must.`;

// ---------------------------------------------------------------------------
// Prompt building (port of llm_arena.py _build_prompt)
// ---------------------------------------------------------------------------

function buildLegalMovesText(jumpSequenceActive, legalJumpMoves, canHalt, planMode) {
  // Placement phase: no legal moves listed
  if (!jumpSequenceActive) return '';

  // Full sequence mode during jump: no legal moves (LLM plans whole sequence)
  if (planMode === 'full_sequence') return '';

  // Single mode during jump sequence: list legal jump destinations + halt
  const parts = [];
  if (legalJumpMoves && legalJumpMoves.length > 0) {
    const jumpStrs = legalJumpMoves.map(([r, c]) => `(${r},${c})`);
    parts.push(`JUMP to: ${jumpStrs.join(', ')}`);
  }
  if (canHalt) {
    parts.push('HALT (end jump sequence)');
  }
  return parts.join('\n');
}

// The system prompt is the rules tutorial + generic instructions. It is
// byte-identical across every turn of every game, so it benefits from
// prompt caching (Anthropic: explicit cache_control; OpenAI: automatic
// prefix caching for prompts > 1024 tokens; Gemini: systemInstruction).
// Walk the tutorial text and wrap each raw 21x15 board block with the
// same coordinate labels the live board uses. A "board block" is any run
// of 21 consecutive lines that are exactly 15 characters wide and contain
// only tile characters. This keeps the tutorial boards visually identical
// to what the model sees during play.
const BOARD_TILE_RE = /^[+\-┼Ox]{15}$/;

function labelTutorialBoards(text) {
  const lines = text.split('\n');
  const out = [];
  for (let i = 0; i < lines.length;) {
    if (i + 21 <= lines.length) {
      const candidate = lines.slice(i, i + 21);
      if (candidate.every(l => BOARD_TILE_RE.test(l))) {
        out.push(...renderLabeledBoardLines(candidate, 15));
        i += 21;
        continue;
      }
    }
    out.push(lines[i]);
    i++;
  }
  return out.join('\n');
}

const LABELED_TUTORIAL_TEXT = labelTutorialBoards(TUTORIAL_TEXT);

export function buildSystemPrompt() {
  return `${LABELED_TUTORIAL_TEXT}

---

You are playing Philosopher's Football. On each turn you will receive the current board state (with coordinate labels printed above and to the left), which player you are, which moves are legal, and any error feedback from a previous attempt.

TURN STRUCTURE

A complete turn is either:
1. A single PLACE action on an empty cell (not the ball, not a deep endzone, not an occupied cell), OR
2. One or more JUMP actions ending with HALT.

HALT is never a complete turn on its own. A turn that begins with JUMP must include at least one landing coordinate before HALT.

You will be told the plan_mode for each turn:

PLAN MODE: single_action
You emit one action per response. Valid actions depend on the turn state:
  - At the start of your turn (no jumps made yet): PLACE (r,c) or JUMP (r,c)
  - After at least one JUMP this turn: JUMP (r,c) or HALT
  - HALT alone at turn start is invalid; PLACE after a JUMP is invalid.

PLAN MODE: full_sequence
You emit the entire turn in one response. Valid responses:
  - PLACE (r,c)
  - JUMP (r1,c1) -> (r2,c2) -> ... -> HALT  (must include at least one landing)
A bare "JUMP -> HALT" with no landings is invalid.

RESPONSE FORMAT (both modes):

REASONING: <your strategic thinking, under 200 words>
MOVE: <your action or full sequence per the mode above>

Your response is read by a program, not a person. If the MOVE line does not
match the format below exactly, the move is REJECTED and nothing is played:
you are asked again with the parse error, and after three rejected responses
no move is made this turn. A correct plan in REASONING counts for nothing if
the MOVE line is malformed. The rules the parser enforces:
  - A JUMP lists LANDING squares only, joined by "->". Do not include the ball's
    starting square or the men jumped over.
  - Nothing but coordinates and arrows may appear between JUMP and HALT. Put any
    explanation in REASONING, not on the MOVE line; text after HALT is ignored.
  - In full_sequence mode a jump without a closing HALT is rejected.
  - Write MOVE: once, as the last line of your response.

Examples:
- Placement (either mode):              MOVE: PLACE (8, 5)
- Single jump, single_action:           MOVE: JUMP (7, 3)
- Halting after a jump, single_action:  MOVE: HALT
- Multi-jump, full_sequence:            MOVE: JUMP (6, 4) -> (4, 4) -> (2, 4) -> HALT
- Single jump, full_sequence:           MOVE: JUMP (7, 3) -> HALT`;
}

// Per-turn content, the only part that changes between calls in a game.
function buildUserPrompt({
  board, currentPlayer, rows, cols, turnNumber,
  jumpSequenceActive, legalJumpMoves, canHalt,
  planMode, errorFeedback, isPuzzle = false,
}) {
  const boardText = renderBoardText(board, currentPlayer, rows, cols, turnNumber, jumpSequenceActive, isPuzzle);

  const goalText = currentPlayer === 1
    ? "Score by landing the ball (O) in the '+++' zone at the TOP (rows 0-1)."
    : "Score by landing the ball (O) in the '---' zone at the BOTTOM (rows " + (rows - 2) + "-" + (rows - 1) + ").";
  // Puzzle positions are mid-game by design; drop the "Turn N" cue so the
  // model evaluates the board on its own merits instead of treating it as the
  // opening (which biases it toward placement even when stones are present).
  let turnInfo = isPuzzle
    ? `You are Player ${currentPlayer}. ${goalText}`
    : `Turn ${turnNumber + 1}. You are Player ${currentPlayer}. ${goalText}`;
  if (jumpSequenceActive) {
    turnInfo += ' (JUMP SEQUENCE ACTIVE, must JUMP or HALT)';
  }

  const orientation = currentPlayer === 1
    ? `Rows 0-${rows - 1} top-to-bottom. You are P1: score in +++ (rows 0-1). Defend --- (rows ${rows - 2}-${rows - 1}).`
    : `Rows 0-${rows - 1} top-to-bottom. You are P2: score in --- (rows ${rows - 2}-${rows - 1}). Defend +++ (rows 0-1).`;

  const legalMovesText = buildLegalMovesText(jumpSequenceActive, legalJumpMoves, canHalt, planMode);
  const legalMovesSection = legalMovesText ? `\nLegal moves:\n${legalMovesText}\n` : '';

  const errorText = errorFeedback
    ? `\nYOUR PREVIOUS MOVE WAS INVALID: ${errorFeedback}\nPlease try again.\n`
    : '';

  const planModeLine = `plan_mode: ${planMode === 'full_sequence' ? 'full_sequence' : 'single_action'}`;

  return `PHUTBALL, ${rows}x${cols} board
${turnInfo}
${orientation}
${planModeLine}

${boardText}
${legalMovesSection}${errorText}`;
}

// ---------------------------------------------------------------------------
// Response parsing (port of llm_arena.py)
// ---------------------------------------------------------------------------

export function parseResponse(rawResponse) {
  // Handle either order: REASONING then MOVE, or MOVE then REASONING
  const reasoningMatch = rawResponse.match(/REASONING:(.*?)MOVE:/is)
    || rawResponse.match(/MOVE:.*?\nREASONING:(.*)/is)
    || rawResponse.match(/REASONING:(.*)/is);
  const reasoning = reasoningMatch ? reasoningMatch[1].trim() : null;

  // The move is everything from the LAST "MOVE:" onward (a model that
  // mentions MOVE: in its reasoning still gets its final answer read), kept
  // multi-line so a long chain wrapped onto the next line is not cut. The
  // grammar in parseMove decides where the move ends.
  const idx = rawResponse.toUpperCase().lastIndexOf('MOVE:');
  const moveStr = idx >= 0 ? rawResponse.slice(idx + 5).trim() : rawResponse.trim();
  return { reasoning, moveStr };
}

// ---------------------------------------------------------------------------
// Move string -> commit_move format
// ---------------------------------------------------------------------------

const PAIR = String.raw`\(\s*\d+\s*,\s*\d+\s*\)`;
// JUMP, then one or more coordinate pairs joined only by arrows, then an
// optional arrow and HALT. Nothing else may appear between the pairs, so a
// stray "(2,10)" in a trailing explanation cannot become a hop.
const JUMP_RE = new RegExp(
  String.raw`^JUMP\s*(${PAIR}(?:\s*->\s*${PAIR})*)\s*(?:->\s*)?(HALT\b)?`, 'i');
const PLACE_RE = new RegExp(String.raw`^PLACE\s*\(\s*(\d+)\s*,\s*(\d+)\s*\)`, 'i');

function normalizeMoveStr(moveStr) {
  return moveStr
    .replace(/[*_`]/g, '')          // markdown emphasis / code ticks
    .replace(/\u2192|=>|\u2013>|\u2014>/g, '->')  // unicode arrows (and en/em dash arrows) to ->
    .replace(/\s+/g, ' ')
    .trim();
}

// Returns { move, error }. move is null when the string is not a legal move
// string for the plan mode; error says why, and is fed back to the model.
//
// full_sequence (puzzles): the whole turn in one line.
//   PLACE (r, c)
//   JUMP (r1, c1) -> (r2, c2) -> ... -> HALT     HALT is required.
// single_action (local games, one hop per call):
//   PLACE (r, c)   |   JUMP (r, c)   |   HALT
export function parseMove(moveStr, planMode = 'full_sequence') {
  const str = normalizeMoveStr(moveStr || '');
  const upper = str.toUpperCase();
  const full = planMode === 'full_sequence';

  if (/^HALT\b/.test(upper)) {
    if (full) return { move: null, error: 'A bare HALT is not a move in full_sequence mode: give PLACE (r, c) or JUMP (r1, c1) -> ... -> HALT' };
    return { move: { type: 'jump', path: [] }, error: null };
  }

  if (upper.startsWith('PLACE')) {
    const m = str.match(PLACE_RE);
    if (!m) return { move: null, error: 'PLACE must be followed by one coordinate pair: PLACE (r, c)' };
    return { move: { type: 'placement', path: [[parseInt(m[1], 10), parseInt(m[2], 10)]] }, error: null };
  }

  if (upper.startsWith('JUMP')) {
    const m = str.match(JUMP_RE);
    if (!m) {
      return { move: null, error: 'JUMP must be coordinate pairs joined only by "->", e.g. JUMP (6, 4) -> (4, 4) -> HALT' };
    }
    const path = [...m[1].matchAll(/\(\s*(\d+)\s*,\s*(\d+)\s*\)/g)]
      .map(x => [parseInt(x[1], 10), parseInt(x[2], 10)]);
    if (full && !m[2]) {
      return { move: null, error: 'The jump sequence must end with -> HALT (only the landings before HALT are played)' };
    }
    if (!full && path.length > 1) {
      return { move: null, error: 'single_action mode: give one landing per response, e.g. JUMP (7, 3)' };
    }
    return { move: { type: 'jump', path }, error: null };
  }

  return { move: null, error: 'Move must start with PLACE, JUMP or HALT' };
}

// ---------------------------------------------------------------------------
// Platform proxy, calls /llm/move on the Cloudflare Worker (paid members)
// ---------------------------------------------------------------------------

async function callPlatformProxy(provider, model, systemPrompt, userPrompt, token) {
  const { apiFetch } = await import('./api');
  const data = await apiFetch('/llm/move', {
    method: 'POST',
    token,
    body: { provider, model, systemPrompt, userPrompt },
  });
  return {
    text: data.text,
    usage: data.usage,
    creditsRemaining: data.creditsRemaining,
  };
}

// ---------------------------------------------------------------------------
// Main exported function
// ---------------------------------------------------------------------------

export async function requestLLMMove({
  provider, model, apiKey,
  board, currentPlayer, rows, cols, turnNumber,
  jumpSequenceActive, legalJumpMoves, canHalt,
  planMode, errorFeedback,
  usePlatformProxy = false, token = null,
  systemPromptOverride = null,
  isPuzzle = false,
}) {
  const startTime = performance.now();

  const systemPrompt = systemPromptOverride || buildSystemPrompt();
  const userPrompt = buildUserPrompt({
    board, currentPlayer, rows, cols, turnNumber,
    jumpSequenceActive, legalJumpMoves, canHalt,
    planMode, errorFeedback, isPuzzle,
  });
  // Legacy reference kept for the returned `prompt` field so downstream
  // telemetry/logs that concatenated the full text still work.
  const prompt = `${systemPrompt}\n\n---\n\n${userPrompt}`;

  try {
    let rawResponse, usage;

    if (usePlatformProxy && token) {
      // Route through platform proxy (paid members, no BYOK key)
      const result = await callPlatformProxy(provider, model, systemPrompt, userPrompt, token);
      rawResponse = result.text;
      usage = result.usage;
    } else {
      // Direct client-side call (BYOK)
      const result = await callProvider(provider, model, apiKey, systemPrompt, userPrompt);
      rawResponse = result.text;
      usage = result.usage;
    }

    const thinkingTime = (performance.now() - startTime) / 1000;
    const tokenUsage = usage ? usage.input + usage.output : null;
    if (usage) usage.costUsd = estimateCostUsd(model, usage);

    const { reasoning, moveStr } = parseResponse(rawResponse);
    const { move, error: parseError } = parseMove(moveStr, planMode);

    if (!move) {
      return {
        move: null,
        reasoning,
        prompt,
        thinkingTime,
        tokenUsage,
        usage,
        rawResponse,
        error: `Could not parse move '${moveStr.slice(0, 80)}': ${parseError}`,
      };
    }

    return { move, reasoning, prompt, thinkingTime, tokenUsage, usage, rawResponse, error: null };
  } catch (err) {
    const thinkingTime = (performance.now() - startTime) / 1000;
    return {
      move: null,
      reasoning: null,
      prompt,
      thinkingTime,
      tokenUsage: null,
      rawResponse: null,
      error: err.message,
    };
  }
}
